import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/AppRoutes';
import { useValidateCenter } from '@/hooks/api/useAdminAuth';
import useApiErrorHandling from '@/hooks/util/useApiErrorHandler';
import useSendEvents from '@/hooks/util/useSendEvents';
import useValidation from '@/hooks/util/useValidation';
import { CenterValidationFormData } from '@/pages/CenterValidation/components/CenterValidationDialog';
import { useCenterValidationSchema } from '@/pages/CenterValidation/schema/CenterValidationSchema';
import { GenericApiResponse, ValidateCenterResponse } from '@/services/api';
import useStore from '@/store';
import { ErrorCode, getErrorMessageFromCode } from '@/utils/errorMessages';

interface ValidationParameters {
  programCode: string;
  password: string;
  adminCode: string;
}

interface TestParameters extends ValidationParameters {
  abortCode: string;
  startCode: string;
}

export type ErrorsState = Partial<TestParameters>;

const mapCodeToFieldErrors = (code: ErrorCode, t: (key: string) => string) => {
  const errors: ErrorsState = {};
  switch (code as ErrorCode) {
    case ErrorCode.InvalidAdminCode: {
      errors.adminCode = getErrorMessageFromCode(code, t);
      break;
    }
    case ErrorCode.InvalidProgramCode: {
      errors.programCode = getErrorMessageFromCode(code, t);
      break;
    }
    case ErrorCode.IncorrectPassword: {
      errors.password = getErrorMessageFromCode(code, t);
      break;
    }
    case ErrorCode.InvalidAbortCode: {
      errors.abortCode = getErrorMessageFromCode(code, t);
      break;
    }
    case ErrorCode.InvalidStartCode: {
      errors.startCode = getErrorMessageFromCode(code, t);
      break;
    }
    case ErrorCode.AdminAndProgramCodeMismatch: {
      errors.adminCode = getErrorMessageFromCode(code, t);
      break;
    }
    default:
  }
  return errors;
};

const getErrors = (errorStates: ErrorsState[]) => {
  return (
    errorStates.find((errorState) =>
      Object.keys(errorState).some((key) => errorState[key as keyof ErrorsState]),
    ) || {}
  );
};

const useCenterValidation = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setCurrentTheme } = useStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { apiErrors, handleApiError, clearApiError } = useApiErrorHandling((code) =>
    mapCodeToFieldErrors(code, t),
  );

  const schema = useCenterValidationSchema();
  const { errors: validationErrors, validate, clearError } = useValidation(schema);
  const { programLoginStartEvent, programLoginFailureEvent, programLoginSuccessEvent } =
    useSendEvents();

  const resetValidation = useCallback(
    (type?: string) => {
      if (type) clearApiError(type);
      if (type) clearError(type);
    },
    [clearError, clearApiError],
  );

  const { mutate: validateCenter } = useValidateCenter({
    onMutate: () => setIsSubmitting(true),
    onError: () => setIsSubmitting(false),
  });

  const onSubmitCenterDetails = useCallback(
    (
      parameters: ValidationParameters,
      onSuccess?: (
        data: GenericApiResponse<ValidateCenterResponse>,
        parameters: ValidationParameters,
      ) => void,
    ) => {
      programLoginStartEvent(parameters.programCode, 'SCHOOL');
      if (validate(parameters)) {
        validateCenter(parameters, {
          onSuccess: (data) => {
            setIsSubmitting(false);
            if (data.data.code) {
              handleApiError(data.data.code);
              programLoginFailureEvent(parameters.programCode, data.data.code.toString());
            } else if (onSuccess) {
              onSuccess(data, parameters);
            }
          },
          onError: () => {
            setIsSubmitting(false);
          },
        });
      } else {
        programLoginFailureEvent(parameters.programCode);
      }
    },
    [validate, validateCenter, handleApiError, programLoginStartEvent, programLoginFailureEvent],
  );

  const onProceed = useCallback(
    (validationResponse: ValidateCenterResponse, parameters: CenterValidationFormData) => {
      const {
        accessToken,
        adminId,
        refreshToken,
        splashScreenImage,
        centerCode,
        centerName,
        programCode,
        isFln,
      } = validationResponse;
      const { adminCode } = parameters;
      localStorage.setItem('accessToken', accessToken.token);
      localStorage.setItem('adminId', adminId);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('splashScreenImage', JSON.stringify(splashScreenImage));
      const adminDetails = {
        centerCode,
        centerName,
        programCode,
        adminCode,
        isFln,
      };
      localStorage.setItem('adminDetails', JSON.stringify(adminDetails));
      // Derive the theme from the program's isFln flag (persisted above so it
      // survives reloads); no build-time env variable is used.
      setCurrentTheme(isFln ? 'fln' : 'default');
      programLoginSuccessEvent(programCode, 'SCHOOL');

      navigate(ROUTES.STUDENT_LOGIN);
    },
    [navigate, programLoginSuccessEvent, setCurrentTheme],
  );

  return {
    onSubmitCenterDetails,
    errors: getErrors([validationErrors, apiErrors]),
    isSubmitting,
    resetValidation,
    onProceed,
  };
};

export default useCenterValidation;
